import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import SeverityBadge from '../components/SeverityBadge';
import TrendBadge from '../components/TrendBadge';
import DataTable from '../components/DataTable';
import useAPI from '../hooks/useAPI';
import { analyzeProject } from '../services/api';

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const { data, loading, reload } = useAPI(() => analyzeProject(projectId, { days }), [projectId, days]);

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Analyzing project...</div>;
  if (!data) return <div style={{ padding: 40, color: '#d63031' }}>Project not found</div>;

  const patterns = data.patterns || [];
  const highRisk = data.high_risk_robots || [];
  const recommendations = data.recommendations || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{data.project}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
            <TrendBadge trend={data.trend} pct={data.trend_change_pct} />
            <span style={{ fontSize: 13, color: '#636e72' }}>
              Analysis period: {data.period_hours >= 24 ? `${Math.round(data.period_hours / 24)}d` : `${data.period_hours}h`}
            </span>
          </div>
        </div>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          style={styles.select}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div style={styles.cardGrid}>
        <StatCard label="Total Errors" value={data.total_errors?.toLocaleString()} color="#d63031" />
        <StatCard label="Unique Codes" value={data.unique_codes} color="#e17055" />
        <StatCard label="Robots Affected" value={data.robots_affected} color="#0984e3" />
        <StatCard label="Critical" value={data.critical_count} color="#c0392b" />
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={styles.sectionTitle}>Recommendations</h2>
          <div style={styles.recsGrid}>
            {recommendations.map((rec, i) => (
              <div key={i} style={styles.recCard}>
                <div style={styles.recPriority}>P{rec.priority}</div>
                <div>
                  <div style={styles.recTitle}>{rec.title}</div>
                  <div style={styles.recDetail}>{rec.detail?.slice(0, 150)}</div>
                  {rec.estimated_impact && (
                    <span style={styles.impactBadge}>Impact: {rec.estimated_impact}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.twoCol}>
        {/* Patterns */}
        <div>
          <h2 style={styles.sectionTitle}>Detected Patterns ({patterns.length})</h2>
          <DataTable
            columns={[
              {
                key: 'type',
                label: 'Type',
                render: v => <span style={styles.patternType}>{v}</span>,
              },
              { key: 'error_code', label: 'Error' },
              { key: 'severity', label: 'Severity', render: v => <SeverityBadge severity={v} /> },
              { key: 'count', label: 'Count', align: 'right' },
              { key: 'robots', label: 'Robots', align: 'right', render: v => v?.length || 0 },
            ]}
            data={patterns.slice(0, 15)}
          />
        </div>

        {/* High-risk robots */}
        <div>
          <h2 style={styles.sectionTitle}>High-Risk Robots ({highRisk.length})</h2>
          <DataTable
            columns={[
              { key: 'robot_number', label: 'Robot #' },
              {
                key: 'risk_score',
                label: 'Risk',
                render: (v, row) => (
                  <span style={{ color: row.risk_level === 'critical' ? '#d63031' : '#e17055', fontWeight: 600 }}>
                    {v}
                  </span>
                ),
              },
              {
                key: 'risk_level',
                label: 'Level',
                render: v => (
                  <span style={{
                    ...styles.riskBadge,
                    background: v === 'critical' ? '#ffe0e0' : v === 'high' ? '#fff3cd' : '#e9ecef',
                    color: v === 'critical' ? '#c0392b' : v === 'high' ? '#d68910' : '#636e72',
                  }}>
                    {v}
                  </span>
                ),
              },
              { key: 'faults_7d', label: 'Faults', align: 'right' },
            ]}
            data={highRisk.slice(0, 15)}
            onRowClick={(row) => navigate(`/robot/${projectId}/${row.robot_number}`)}
          />
        </div>
      </div>

      {/* Parts Forecast */}
      {data.parts_forecast?.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={styles.sectionTitle}>Parts Forecast</h2>
          <DataTable
            columns={[
              { key: 'part_id', label: 'Part ID' },
              { key: 'description', label: 'Description' },
              { key: 'category', label: 'Category' },
              { key: 'estimated_need', label: 'Est. Need', align: 'right' },
              { key: 'unit_cost', label: 'Unit $', align: 'right', render: v => `$${v?.toFixed(2)}` },
            ]}
            data={data.parts_forecast.slice(0, 10)}
          />
        </div>
      )}
    </div>
  );
}

const styles = {
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    margin: '20px 0',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  select: {
    padding: '8px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 13,
    background: '#fff',
  },
  recsGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  recCard: {
    display: 'flex',
    gap: 14,
    background: '#fff',
    borderRadius: 8,
    padding: '14px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    alignItems: 'flex-start',
  },
  recPriority: {
    background: '#6c5ce7',
    color: '#fff',
    borderRadius: 6,
    padding: '4px 10px',
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  recTitle: { fontWeight: 600, fontSize: 14, marginBottom: 4 },
  recDetail: { fontSize: 13, color: '#636e72', lineHeight: 1.4 },
  impactBadge: {
    display: 'inline-block',
    marginTop: 6,
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 4,
    background: '#dfe6e9',
    color: '#636e72',
    fontWeight: 600,
  },
  patternType: {
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 3,
    background: '#dfe6e9',
    fontWeight: 500,
  },
  riskBadge: {
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
  },
};
